import { useEffect, useState, useMemo, useCallback, useRef, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLoaderData } from "react-router-dom";
import toast from "react-hot-toast";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore, useOrganizationStore } from "../../store/organization";
import { userStore } from "../../store/user";
import { eventStore, useEventStore } from "../../store/event";
import {
  GET_ALL_VENDORS,
  GET_WORKERS_BY_VENDOR,
  GET_ROLES_BY_VENDOR,
  GET_TIMECLOCK_SUMMARY_BY_VENDOR,
  GET_EVENTS_FOR_ORG,
  GET_ACTIVE_SHIFTS_BY_VENDOR,
  GET_CLERKS_FOR_ORG,
  GET_TIMECLOCK_SETTINGS,
} from "./queries";
import {
  CREATE_WORKER,
  UPDATE_WORKER,
  DELETE_WORKER,
  DEACTIVATE_WORKER,
  CREATE_ROLE,
  UPDATE_ROLE,
  DELETE_ROLE,
  DEACTIVATE_ROLE,
  INSERT_TIMECLOCK_ENTRY,
  UPDATE_TIMECLOCK_ENTRY,
  DELETE_TIMECLOCK_ENTRY,
  BULK_INSERT_WORKERS,
  SET_WORKER_ROLES,
  UPSERT_TIMECLOCK_SETTINGS,
} from "./mutations";

interface Clerk {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string | null;
  phone_number: string | null;
}

const client = createApolloClient();
const { getState: getOrgState } = organizationStore;
const { getState: getUserState } = userStore;
const { getState: getEventState } = eventStore;

interface Worker {
  id: number;
  vendor_id: number;
  event_id: number;
  name: string;
  hourly_rate: number | null;
  pin: string | null;
  phone: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  worker_role_assignments: Array<{
    worker_role: { id: number; name: string };
  }>;
  timeclock_entries_aggregate?: { aggregate: { count: number } };
}

interface WorkerRole {
  id: number;
  vendor_id: number;
  name: string;
  is_active: boolean;
  worker_role_assignments_aggregate?: { aggregate: { count: number } };
}

interface Vendor {
  id: number;
  name: string;
  is_active: boolean;
}

interface TimeclockEntry {
  id: number;
  worker_id: number;
  event_id: number;
  clock_in: string;
  clock_out: string | null;
  worker: { id: number; name: string; hourly_rate: number | null };
}

interface Event {
  id: number;
  name: string;
}

interface ActiveShift {
  id: number;
  worker_id: number;
  event_id: number;
  clock_in: string;
  worker: { id: number; name: string; hourly_rate: number | null };
  event: { id: number; name: string };
}

// ===== Helpers =====
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const formatDuration = (clockIn: string, clockOut: string | null) => {
  if (!clockOut) return "Active";
  const ms = new Date(clockOut).getTime() - new Date(clockIn).getTime();
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatRunningDuration = (clockIn: string, now: number) => {
  const ms = Math.max(0, now - new Date(clockIn).getTime());
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
};

// ===== Stat Card =====
const StatCard = ({ label, value, color = "text-gray-900" }: { label: string; value: string | number; color?: string }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
    <span className={`text-lg font-bold ${color} tabular-nums`}>{value}</span>
  </div>
);

// ===== Info Tooltip =====
// Tooltip is rendered into a portal so it isn't clipped by ancestor `overflow-hidden`
// (e.g. the card containers used here). Position is computed from the trigger's
// bounding rect each time it opens.
const InfoTooltip = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setCoords({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
    }
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-flex ml-1.5 cursor-help"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        aria-label={text}
      >
        <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
      </span>
      {open && coords && createPortal(
        <span
          role="tooltip"
          style={{ position: "fixed", top: coords.top, left: coords.left, transform: "translateX(-50%)", zIndex: 1000 }}
          className="px-3 py-2 rounded-md shadow-lg bg-gray-800 text-xs font-normal text-white w-72 normal-case tracking-normal pointer-events-none"
        >
          {text}
        </span>,
        document.body,
      )}
    </>
  );
};

// ===== Worker roles editor =====
// Click the roles cell to open a popover with a checklist of the vendor's active
// roles. Save calls SET_WORKER_ROLES (delete-all + reinsert) so the worker's role
// set matches the checked boxes exactly.
const WorkerRolesEditor = ({
  worker,
  roles,
  onSave,
}: {
  worker: Worker;
  roles: WorkerRole[];
  onSave: (workerId: number, roleIds: number[]) => Promise<void>;
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [draft, setDraft] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setCoords({ top: rect.bottom + 4, left: rect.left });
    setDraft(new Set(worker.worker_role_assignments.map((a) => a.worker_role.id)));
    setOpen(true);
  };
  const hide = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") hide(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const toggle = (id: number) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(worker.id, Array.from(draft));
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const activeRoles = roles.filter((r) => r.is_active);
  const display = worker.worker_role_assignments.map((a) => a.worker_role.name).join(", ");

  return (
    <>
      <span
        ref={triggerRef}
        onClick={show}
        className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors text-xs text-gray-500 inline-block"
      >
        {display || <span className="text-gray-300">No roles · click to assign</span>}
      </span>
      {open && coords && createPortal(
        <>
          <div className="fixed inset-0 z-[999]" onClick={hide} />
          <div
            style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 1000 }}
            className="bg-white rounded-lg border border-gray-200 shadow-lg p-2 min-w-[220px]"
            onClick={(e) => e.stopPropagation()}
          >
            {activeRoles.length === 0 ? (
              <div className="text-xs text-gray-500 px-2 py-2">
                No active roles for this vendor. Add roles in the Roles section above.
              </div>
            ) : (
              <>
                <div className="max-h-60 overflow-y-auto">
                  {activeRoles.map((r) => (
                    <label
                      key={r.id}
                      className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer rounded"
                    >
                      <input
                        type="checkbox"
                        checked={draft.has(r.id)}
                        onChange={() => toggle(r.id)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">{r.name}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-2 pt-2 mt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={hide}
                    className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400"
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

// ===== Inline editable cell =====
const EditableCell = ({
  value,
  onSave,
  type = "text",
  placeholder = "",
  className = "",
  maxLength,
  inputMode,
  digitsOnly = false,
}: {
  value: string | number | null;
  onSave: (val: string) => void;
  type?: string;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "search" | "email" | "url" | "none";
  digitsOnly?: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  useEffect(() => { setDraft(String(value ?? "")); }, [value]);

  const sanitize = (val: string) => {
    let next = digitsOnly ? val.replace(/\D/g, "") : val;
    if (maxLength != null) next = next.slice(0, maxLength);
    return next;
  };

  if (!editing) {
    return (
      <span
        className={`cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors ${className}`}
        onClick={() => setEditing(true)}
      >
        {value ?? <span className="text-gray-300">{placeholder || "—"}</span>}
      </span>
    );
  }

  return (
    <input
      type={type}
      className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full"
      value={draft}
      maxLength={maxLength}
      inputMode={inputMode}
      onChange={(e) => setDraft(sanitize(e.target.value))}
      onBlur={() => { onSave(sanitize(draft)); setEditing(false); }}
      onKeyDown={(e) => { if (e.key === "Enter") { onSave(sanitize(draft)); setEditing(false); } if (e.key === "Escape") setEditing(false); }}
      autoFocus
    />
  );
};

// ===== Toggle badge =====
const ActiveBadge = ({ active, onClick }: { active: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
      active ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-red-100 text-red-800 hover:bg-red-200"
    }`}
  >
    {active ? "Active" : "Inactive"}
  </button>
);

// ===== Modal =====
// Rendered into a portal so the backdrop sits above ancestors with overflow-hidden.
// Click backdrop or Esc to close; click card itself doesn't propagate.
const Modal = ({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-400 hover:text-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

// ===== Clock-in form =====
const ClockInForm = ({
  events,
  workers,
  activeShifts,
  onSubmit,
  onCancel,
}: {
  events: Event[];
  workers: Worker[];
  activeShifts: ActiveShift[];
  onSubmit: (workerId: number, eventId: number) => void;
  onCancel: () => void;
}) => {
  const defaultEventId = getEventState().eventId;
  const [eventId, setEventId] = useState<number | null>(defaultEventId ? Number(defaultEventId) : null);
  const [workerId, setWorkerId] = useState<number | null>(null);

  const conflict = useMemo(
    () => (workerId != null ? activeShifts.find((s) => s.worker_id === workerId) ?? null : null),
    [workerId, activeShifts],
  );

  const canSubmit = eventId != null && workerId != null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(workerId!, eventId!);
      }}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Event</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
            value={eventId ?? ""}
            onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : null)}
            required
            autoFocus
          >
            <option value="">Choose an event...</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Worker</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
            value={workerId ?? ""}
            onChange={(e) => setWorkerId(e.target.value ? Number(e.target.value) : null)}
            required
          >
            <option value="">Choose a worker...</option>
            {workers
              .filter((w) => w.is_active)
              .map((w) => (
                <option key={w.id} value={w.id}>{w.name || `Worker #${w.id}`}</option>
              ))}
          </select>
        </div>
        {conflict && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Already clocked in at <span className="font-semibold">{conflict.event.name}</span> since{" "}
            {formatTime(conflict.clock_in)}. Clocking in again will create a second open shift.
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Clock in
        </button>
      </div>
    </form>
  );
};

// ===== Missed shift form =====
const MissedShiftForm = ({
  events,
  workers,
  onSubmit,
  onCancel,
}: {
  events: Event[];
  workers: Worker[];
  onSubmit: (workerId: number, eventId: number, clockIn: string, clockOut: string) => void;
  onCancel: () => void;
}) => {
  const defaultEventId = getEventState().eventId;
  const [eventId, setEventId] = useState<number | null>(defaultEventId ? Number(defaultEventId) : null);
  const [workerId, setWorkerId] = useState<number | null>(null);
  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");

  const ordered = clockIn && clockOut ? new Date(clockOut) > new Date(clockIn) : true;
  const canSubmit = eventId != null && workerId != null && !!clockIn && !!clockOut && ordered;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(workerId!, eventId!, clockIn, clockOut);
      }}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Event</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
            value={eventId ?? ""}
            onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : null)}
            required
            autoFocus
          >
            <option value="">Choose an event...</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Worker</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
            value={workerId ?? ""}
            onChange={(e) => setWorkerId(e.target.value ? Number(e.target.value) : null)}
            required
          >
            <option value="">Choose a worker...</option>
            {workers
              .filter((w) => w.is_active)
              .map((w) => (
                <option key={w.id} value={w.id}>{w.name || `Worker #${w.id}`}</option>
              ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Clock in</label>
            <input
              type="datetime-local"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              value={clockIn}
              onChange={(e) => setClockIn(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Clock out</label>
            <input
              type="datetime-local"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              value={clockOut}
              onChange={(e) => setClockOut(e.target.value)}
              required
            />
          </div>
        </div>
        {!ordered && (
          <div className="text-xs text-red-600">Clock out must be after clock in.</div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add shift
        </button>
      </div>
    </form>
  );
};

// ===== Name prompt form (reused for add-role and add-worker) =====
const NamePromptForm = ({
  label,
  placeholder,
  submitText,
  onSubmit,
  onCancel,
}: {
  label: string;
  placeholder: string;
  submitText: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) => {
  const [name, setName] = useState("");
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(trimmed);
      }}
    >
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</label>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 outline-none"
        />
      </div>
      <div className="flex items-center justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitText}
        </button>
      </div>
    </form>
  );
};

// ===== Add-worker form (name + event) =====
const AddWorkerForm = ({
  events,
  onSubmit,
  onCancel,
}: {
  events: Event[];
  onSubmit: (name: string, eventId: number) => void;
  onCancel: () => void;
}) => {
  const defaultEventId = getEventState().eventId;
  const [name, setName] = useState("");
  const [eventId, setEventId] = useState<number | null>(defaultEventId ? Number(defaultEventId) : null);
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && eventId != null;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(trimmed, eventId!);
      }}
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Worker name</label>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jane Smith"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Event</label>
          <select
            value={eventId ?? ""}
            onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : null)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
          >
            <option value="">Choose an event...</option>
            {events.map((evt) => (
              <option key={evt.id} value={evt.id}>{evt.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add worker
        </button>
      </div>
    </form>
  );
};

// ===== Loader =====
export const timeclockManagerLoader = async () => {
  const organizationId = getOrgState().organizationId;
  const user = getUserState().user;

  let where: any = {};
  if (organizationId) where.organization_id = { _eq: organizationId };
  if ((user?.vendors?.length || 0) > 0) where.id = { _in: user?.vendors };

  const eventsWhere = organizationId ? { organization_id: { _eq: organizationId } } : {};

  const [vendorsRes, eventsRes] = await Promise.all([
    client.query({ query: GET_ALL_VENDORS, variables: { where } }),
    client.query({ query: GET_EVENTS_FOR_ORG, variables: { where: eventsWhere } }),
  ]);
  return {
    vendors: vendorsRes.data.vendors as Vendor[],
    events: eventsRes.data.events as Event[],
  };
};

// ===== Page =====
export const TimeclockManagerPage = () => {
  const loaderData = useLoaderData() as { vendors: Vendor[]; events: Event[] };
  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const headerEventId = useEventStore((s) => s.eventId);
  const [vendors, setVendors] = useState<Vendor[]>(loaderData.vendors);
  const [events, setEvents] = useState<Event[]>(loaderData.events);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [roles, setRoles] = useState<WorkerRole[]>([]);
  const [timeclockEntries, setTimeclockEntries] = useState<TimeclockEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeShifts, setActiveShifts] = useState<ActiveShift[]>([]);
  const [clockInModalOpen, setClockInModalOpen] = useState(false);
  const [missedShiftModalOpen, setMissedShiftModalOpen] = useState(false);
  const [importClerksModalOpen, setImportClerksModalOpen] = useState(false);
  const [clerks, setClerks] = useState<Clerk[]>([]);
  const [clerksLoading, setClerksLoading] = useState(false);
  const [selectedClerkIds, setSelectedClerkIds] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importEventId, setImportEventId] = useState<number | null>(null);
  const [clerkSearch, setClerkSearch] = useState("");
  const [requirePin, setRequirePin] = useState(false);
  const [savingRequirePin, setSavingRequirePin] = useState(false);
  const [addWorkerModalOpen, setAddWorkerModalOpen] = useState(false);
  const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Tick every 30s so the running-duration column on Active shifts stays fresh.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Refetch vendors + events when the header org or event changes. Vendors are
  // filtered to those participating in the header event when one is selected.
  useEffect(() => {
    if (!headerOrgId) {
      setVendors([]);
      setEvents([]);
      return;
    }
    const userVendors = getUserState().user?.vendors ?? [];
    const vendorWhere: any = { organization_id: { _eq: Number(headerOrgId) } };
    if (userVendors.length > 0) vendorWhere.id = { _in: userVendors };
    if (headerEventId) {
      vendorWhere.locations = {
        event_id: { _eq: Number(headerEventId) },
        is_active: { _eq: true },
      };
    }
    Promise.all([
      client.query({ query: GET_ALL_VENDORS, variables: { where: vendorWhere }, fetchPolicy: "network-only" }),
      client.query({
        query: GET_EVENTS_FOR_ORG,
        variables: { where: { organization_id: { _eq: Number(headerOrgId) } } },
        fetchPolicy: "network-only",
      }),
    ])
      .then(([vRes, eRes]) => {
        setVendors(vRes.data.vendors);
        setEvents(eRes.data.events);
      })
      .catch((err: any) => toast.error("Failed to refresh data: " + (err?.message ?? "")));
  }, [headerOrgId, headerEventId]);

  // Clear the selected vendor if it's no longer in the (possibly event-filtered) list.
  useEffect(() => {
    if (selectedVendorId != null && !vendors.some((v) => v.id === selectedVendorId)) {
      setSelectedVendorId(null);
    }
  }, [vendors, selectedVendorId]);

  // Pre-fetch the org's Clerks so the import button can show its enabled/disabled
  // state (and reason) before the user clicks it.
  useEffect(() => {
    if (!headerOrgId) {
      setClerks([]);
      return;
    }
    setClerksLoading(true);
    client.query({
      query: GET_CLERKS_FOR_ORG,
      variables: { organizationId: Number(headerOrgId) },
      fetchPolicy: "network-only",
    })
      .then(({ data }) => {
        const seen = new Set<string>();
        const deduped: Clerk[] = [];
        for (const u of data.users as Clerk[]) {
          const key =
            u.email?.trim().toLowerCase() ||
            u.username?.trim().toLowerCase() ||
            `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim().toLowerCase();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          deduped.push(u);
        }
        setClerks(deduped);
      })
      .catch((err: any) => toast.error("Failed to load clerks: " + (err?.message ?? "")))
      .finally(() => setClerksLoading(false));
  }, [headerOrgId]);

  const selectedVendor = useMemo(() => vendors.find((v) => v.id === selectedVendorId), [vendors, selectedVendorId]);

  const fetchData = useCallback(async (vendorId: number) => {
    setIsLoading(true);
    try {
      const eventId = headerEventId ? Number(headerEventId) : null;
      const workerWhere: any = { vendor_id: { _eq: vendorId } };
      if (eventId) workerWhere.event_id = { _eq: eventId };
      const entryWhere: any = { worker: { vendor_id: { _eq: vendorId } } };
      if (eventId) entryWhere.event_id = { _eq: eventId };
      const activeWhere: any = { ...entryWhere, clock_out: { _is_null: true } };

      const [w, r, tc, as, settings] = await Promise.all([
        client.query({ query: GET_WORKERS_BY_VENDOR, variables: { where: workerWhere }, fetchPolicy: "network-only" }),
        client.query({ query: GET_ROLES_BY_VENDOR, variables: { vendorId }, fetchPolicy: "network-only" }),
        client.query({ query: GET_TIMECLOCK_SUMMARY_BY_VENDOR, variables: { where: entryWhere }, fetchPolicy: "network-only" }),
        client.query({ query: GET_ACTIVE_SHIFTS_BY_VENDOR, variables: { where: activeWhere }, fetchPolicy: "network-only" }),
        client.query({ query: GET_TIMECLOCK_SETTINGS, variables: { vendorId }, fetchPolicy: "network-only" }),
      ]);
      setWorkers(w.data.workers);
      setRoles(r.data.worker_roles);
      setActiveShifts(as.data.timeclock_entries);
      setTimeclockEntries(tc.data.timeclock_entries);
      setRequirePin(!!settings.data?.timeclock_settings?.[0]?.require_pin);
    } catch { toast.error("Failed to load vendor data"); }
    finally { setIsLoading(false); }
  }, [headerEventId]);

  useEffect(() => {
    if (selectedVendorId) {
      fetchData(selectedVendorId);
    } else {
      setWorkers([]); setRoles([]); setTimeclockEntries([]); setActiveShifts([]);
    }
  }, [selectedVendorId, fetchData]);

  const fetchActiveShifts = useCallback(async (vendorId: number) => {
    try {
      const eventId = headerEventId ? Number(headerEventId) : null;
      const where: any = {
        worker: { vendor_id: { _eq: vendorId } },
        clock_out: { _is_null: true },
      };
      if (eventId) where.event_id = { _eq: eventId };
      const { data } = await client.query({
        query: GET_ACTIVE_SHIFTS_BY_VENDOR,
        variables: { where },
        fetchPolicy: "network-only",
      });
      setActiveShifts(data.timeclock_entries);
    } catch { /* keep prior state on error */ }
  }, [headerEventId]);

  const refresh = () => { if (selectedVendorId) fetchData(selectedVendorId); };

  const toggleRequirePin = async (next: boolean) => {
    if (!selectedVendorId) return;
    const prev = requirePin;
    setRequirePin(next);
    setSavingRequirePin(true);
    try {
      await client.mutate({
        mutation: UPSERT_TIMECLOCK_SETTINGS,
        variables: { vendor_id: selectedVendorId, require_pin: next },
      });
    } catch (err: any) {
      setRequirePin(prev);
      toast.error("Failed to update setting: " + (err?.message ?? ""));
    } finally {
      setSavingRequirePin(false);
    }
  };

  // ===== Import Clerks as workers =====
  const openImportClerksModal = () => {
    if (!selectedVendorId || clerks.length === 0) return;
    setSelectedClerkIds(new Set());
    setImportEventId(headerEventId ? Number(headerEventId) : null);
    setClerkSearch("");
    setImportClerksModalOpen(true);
  };

  const filteredClerks = useMemo(() => {
    const q = clerkSearch.trim().toLowerCase();
    if (!q) return clerks;
    return clerks.filter((c) => {
      const fullName = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim().toLowerCase();
      return (
        fullName.includes(q) ||
        (c.username?.toLowerCase().includes(q) ?? false) ||
        (c.email?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [clerks, clerkSearch]);

  const toggleClerkSelected = (id: number) => {
    setSelectedClerkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const importSelectedClerks = async () => {
    if (!selectedVendorId || selectedClerkIds.size === 0 || importEventId == null) return;
    const toImport = clerks.filter((c) => selectedClerkIds.has(c.id));
    const objects = toImport.map((c) => ({
      vendor_id: selectedVendorId,
      event_id: importEventId,
      name: `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || c.username,
      phone: c.phone_number || null,
      is_active: true,
    }));
    setImporting(true);
    try {
      const res = await client.mutate({ mutation: BULK_INSERT_WORKERS, variables: { objects } });
      const count = res.data?.insert_workers?.affected_rows ?? 0;
      toast.success(`Imported ${count} clerk${count === 1 ? "" : "s"} as worker${count === 1 ? "" : "s"}`);
      setImportClerksModalOpen(false);
      refresh();
    } catch (err: any) {
      toast.error("Failed to import clerks: " + (err?.message ?? ""));
    } finally {
      setImporting(false);
    }
  };

  const setWorkerRoles = async (workerId: number, roleIds: number[]) => {
    try {
      await client.mutate({
        mutation: SET_WORKER_ROLES,
        variables: {
          workerId,
          objects: roleIds.map((id) => ({ worker_id: workerId, role_id: id })),
        },
      });
      toast.success("Roles updated");
      refresh();
    } catch (err: any) {
      toast.error("Failed to update roles: " + (err?.message ?? ""));
    }
  };

  // ===== Worker CRUD =====
  const createWorker = async (name: string, eventId: number) => {
    if (!selectedVendorId) return;
    try {
      await client.mutate({ mutation: CREATE_WORKER, variables: { vendor_id: selectedVendorId, event_id: eventId, name, is_active: true } });
      toast.success("Worker created");
      setAddWorkerModalOpen(false);
      refresh();
    } catch (err: any) {
      toast.error("Failed to create worker: " + (err?.message ?? ""));
    }
  };

  const updateWorker = async (id: number, field: string, value: any) => {
    try {
      await client.mutate({ mutation: UPDATE_WORKER, variables: { id, data: { [field]: value, updated_at: new Date().toISOString() } } });
      refresh();
    } catch (err: any) {
      toast.error("Failed to update worker: " + (err?.message ?? ""));
    }
  };

  const deleteWorker = async (id: number) => {
    const target = workers.find((w) => w.id === id);
    if (!target) return;
    const entryCount = target.timeclock_entries_aggregate?.aggregate.count ?? 0;
    const name = target.name || "this worker";
    const confirmMsg = entryCount > 0
      ? `Deactivate ${name}? They have ${entryCount} time card${entryCount === 1 ? "" : "s"} on file, which will be preserved for payroll. They'll be removed from the POS clock-in list and you can re-activate them anytime.`
      : `Permanently delete ${name}? They have no time cards on file, so this can't affect payroll. This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      if (entryCount > 0) {
        await client.mutate({ mutation: DEACTIVATE_WORKER, variables: { id } });
        setWorkers((prev) => prev.map((w) => (w.id === id ? { ...w, is_active: false } : w)));
        toast.success("Worker deactivated");
      } else {
        await client.mutate({ mutation: DELETE_WORKER, variables: { id } });
        setWorkers((prev) => prev.filter((w) => w.id !== id));
        toast.success("Worker deleted");
      }
    } catch (err: any) {
      toast.error("Failed to remove worker: " + (err?.message ?? ""));
    }
  };

  // ===== Role CRUD =====
  const createRole = async (name: string) => {
    if (!selectedVendorId) return;
    try {
      await client.mutate({ mutation: CREATE_ROLE, variables: { vendor_id: selectedVendorId, name, is_active: true } });
      toast.success("Role created");
      setAddRoleModalOpen(false);
      refresh();
    } catch (err: any) {
      toast.error("Failed to create role: " + (err?.message ?? ""));
    }
  };

  const updateRole = async (id: number, field: string, value: any) => {
    try {
      await client.mutate({ mutation: UPDATE_ROLE, variables: { id, data: { [field]: value, updated_at: new Date().toISOString() } } });
      refresh();
    } catch (err: any) {
      toast.error("Failed to update role: " + (err?.message ?? ""));
    }
  };

  const deleteRole = async (id: number) => {
    const target = roles.find((r) => r.id === id);
    if (!target) return;
    const assignmentCount = target.worker_role_assignments_aggregate?.aggregate.count ?? 0;
    const name = target.name || "this role";
    const confirmMsg = assignmentCount > 0
      ? `Deactivate ${name}? It's assigned to ${assignmentCount} worker${assignmentCount === 1 ? "" : "s"}. The role will be hidden from new assignments but the existing assignment history will be preserved.`
      : `Permanently delete ${name}? It isn't assigned to anyone, so this is safe.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      if (assignmentCount > 0) {
        await client.mutate({ mutation: DEACTIVATE_ROLE, variables: { id } });
        setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, is_active: false } : r)));
        toast.success("Role deactivated");
      } else {
        await client.mutate({ mutation: DELETE_ROLE, variables: { id } });
        setRoles((prev) => prev.filter((r) => r.id !== id));
        toast.success("Role deleted");
      }
    } catch (err: any) {
      toast.error("Failed to remove role: " + (err?.message ?? ""));
    }
  };

  // ===== Clock in / out / missed shift =====
  const clockInWorker = async (workerId: number, eventId: number) => {
    try {
      await client.mutate({
        mutation: INSERT_TIMECLOCK_ENTRY,
        variables: {
          worker_id: workerId,
          event_id: eventId,
          clock_in: new Date().toISOString(),
          clock_out: null,
        },
      });
      toast.success("Clocked in");
      setClockInModalOpen(false);
      if (selectedVendorId) refresh();
    } catch (err: any) {
      toast.error("Failed to clock in: " + (err?.message ?? ""));
    }
  };

  const clockOutShift = async (entryId: number) => {
    try {
      await client.mutate({
        mutation: UPDATE_TIMECLOCK_ENTRY,
        variables: { id: entryId, data: { clock_out: new Date().toISOString() } },
      });
      toast.success("Clocked out");
      if (selectedVendorId) refresh();
    } catch (err: any) {
      toast.error("Failed to clock out: " + (err?.message ?? ""));
    }
  };

  const addMissedShift = async (workerId: number, eventId: number, clockIn: string, clockOut: string) => {
    try {
      await client.mutate({
        mutation: INSERT_TIMECLOCK_ENTRY,
        variables: {
          worker_id: workerId,
          event_id: eventId,
          clock_in: new Date(clockIn).toISOString(),
          clock_out: new Date(clockOut).toISOString(),
        },
      });
      toast.success("Shift added");
      setMissedShiftModalOpen(false);
      if (selectedVendorId) refresh();
    } catch (err: any) {
      toast.error("Failed to add shift: " + (err?.message ?? ""));
    }
  };

  // ===== Timeclock CRUD =====
  const updateEntry = async (id: number, field: string, value: any) => {
    try {
      await client.mutate({ mutation: UPDATE_TIMECLOCK_ENTRY, variables: { id, data: { [field]: value } } });
      if (selectedVendorId) refresh();
    } catch (err: any) {
      toast.error("Failed to update entry: " + (err?.message ?? ""));
    }
  };

  const deleteEntry = async (id: number) => {
    try {
      await client.mutate({ mutation: DELETE_TIMECLOCK_ENTRY, variables: { id } });
      setTimeclockEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry deleted");
    } catch (err: any) {
      toast.error("Failed to delete entry: " + (err?.message ?? ""));
    }
  };

  const activeWorkers = workers.filter((w) => w.is_active);
  const inactiveWorkers = workers.filter((w) => !w.is_active);
  const activeRoles = roles.filter((r) => r.is_active);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900">Timeclock manager</h1>
          <button
            type="button"
            onClick={refresh}
            disabled={!selectedVendorId || isLoading}
            title={!selectedVendorId ? "Select a vendor first" : "Refresh data"}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className={`w-3.5 h-3.5 ${isLoading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Vendor</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
              value={selectedVendorId ?? ""}
              onChange={(e) => setSelectedVendorId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Choose a vendor...</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} {!v.is_active ? "(inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={openImportClerksModal}
            disabled={!selectedVendorId || clerksLoading || clerks.length === 0}
            title={
              !selectedVendorId
                ? "Select a vendor first"
                : clerksLoading
                  ? "Loading clerks..."
                  : clerks.length === 0
                    ? "No active Clerks in this organization"
                    : ""
            }
            className="inline-flex items-center gap-1 px-3 py-2.5 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Import Clerks
          </button>
          {!clerksLoading && clerks.length === 0 && (
            <span className="text-xs text-gray-400 italic pb-2">
              No active Clerks in this organization
            </span>
          )}
        </div>

        {selectedVendor && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-5 pt-5 border-t border-gray-100">
            <StatCard label="Active workers" value={activeWorkers.length} />
            <StatCard label="Inactive workers" value={inactiveWorkers.length} color="text-gray-400" />
            <StatCard label="Roles" value={activeRoles.length} />
            <StatCard label="Timesheets" value={timeclockEntries.length} />
            <StatCard label="Vendor ID" value={selectedVendor.id} color="text-gray-400" />
          </div>
        )}

        {selectedVendor && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requirePin}
                disabled={savingRequirePin}
                onChange={(e) => toggleRequirePin(e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                Require PIN to clock in/out
              </span>
              {savingRequirePin && <span className="text-xs text-gray-400">saving...</span>}
            </label>
          </div>
        )}
      </div>

      {isLoading && <div className="text-center py-12 text-gray-400">Loading...</div>}

      {selectedVendorId && !isLoading && (
        <>
          {/* Active shifts card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">Active shifts</h2>
                <span className="text-xs text-gray-400 tabular-nums">
                  {activeShifts.length} on the clock
                </span>
              </div>
              <button
                onClick={() => setClockInModalOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                + Clock in worker
              </button>
            </div>
            {activeShifts.length > 0 ? (
              <>
                <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <div className="col-span-3">Worker</div>
                  <div className="col-span-3">Event</div>
                  <div className="col-span-2">Clocked in</div>
                  <div className="col-span-2">On the clock</div>
                  <div className="col-span-2"></div>
                </div>
                <div className="divide-y divide-gray-100">
                  {activeShifts.map((shift) => (
                    <div
                      key={shift.id}
                      className="grid grid-cols-1 md:grid-cols-12 gap-2 px-5 py-3 hover:bg-gray-50 transition-colors items-center"
                    >
                      <div className="col-span-3 text-sm font-medium text-gray-700">
                        {shift.worker.name || `Worker #${shift.worker.id}`}
                      </div>
                      <div className="col-span-3 text-sm text-gray-500">{shift.event.name}</div>
                      <div className="col-span-2 text-sm text-gray-600">
                        {formatDate(shift.clock_in)} {formatTime(shift.clock_in)}
                      </div>
                      <div className="col-span-2 text-sm text-gray-600 tabular-nums">
                        {formatRunningDuration(shift.clock_in, now)}
                      </div>
                      <div className="col-span-2 text-right">
                        <button
                          onClick={() => clockOutShift(shift.id)}
                          className="text-xs font-semibold text-red-600 hover:text-red-700 transition-colors"
                        >
                          Clock out
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">
                No one is currently clocked in.
              </div>
            )}
          </div>

          {/* Roles card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center">
                <h2 className="text-base font-semibold text-gray-900">Roles</h2>
                <InfoTooltip text="Roles assigned to one or more workers can only be deactivated to preserve assignment history. To fully delete a role, first remove it from every worker." />
              </div>
              <button onClick={() => setAddRoleModalOpen(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">
                + Add role
              </button>
            </div>
            {roles.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {roles.map((role) => (
                  <div key={role.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <EditableCell value={role.name} onSave={(val) => updateRole(role.id, "name", val)} className="text-sm font-medium text-gray-900" placeholder="Role name" />
                    </div>
                    <div className="flex items-center gap-3">
                      <ActiveBadge active={role.is_active} onClick={() => updateRole(role.id, "is_active", !role.is_active)} />
                      {role.is_active && (
                        <button onClick={() => deleteRole(role.id)} className="text-xs text-gray-400 hover:text-red-600 transition-colors">
                          {(role.worker_role_assignments_aggregate?.aggregate.count ?? 0) > 0 ? "Deactivate" : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">No roles defined</div>
            )}
          </div>

          {/* Workers card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center">
                <h2 className="text-base font-semibold text-gray-900">Workers</h2>
                <InfoTooltip text="Workers with time cards on file can only be deactivated to preserve payroll history. To fully delete a worker, first delete their time cards on the Timeclock Report or in the Timesheets section below." />
              </div>
              <button onClick={() => setAddWorkerModalOpen(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">
                + Add worker
              </button>
            </div>
            {workers.length > 0 ? (
              <>
                <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <div className="col-span-4">Name</div>
                  <div className="col-span-2">Roles</div>
                  <div className="col-span-1">Rate</div>
                  <div className="col-span-2">Phone</div>
                  <div className="col-span-1">PIN</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="divide-y divide-gray-100">
                  {workers.map((worker) => (
                    <div key={worker.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-5 py-3 hover:bg-gray-50 transition-colors items-center">
                      <div className="col-span-4">
                        <EditableCell value={worker.name} onSave={(val) => updateWorker(worker.id, "name", val)} className="text-sm font-medium text-gray-900" placeholder="Worker name" />
                      </div>
                      <div className="col-span-2">
                        <WorkerRolesEditor worker={worker} roles={roles} onSave={setWorkerRoles} />
                      </div>
                      <div className="col-span-1">
                        <EditableCell value={worker.hourly_rate != null ? `$${Number(worker.hourly_rate).toFixed(2)}` : null} onSave={(val) => { const num = parseFloat(val.replace(/[^0-9.-]/g, "")); updateWorker(worker.id, "hourly_rate", isNaN(num) ? null : num); }} className="text-sm text-gray-700 tabular-nums" placeholder="$0.00" />
                      </div>
                      <div className="col-span-2">
                        <EditableCell value={worker.phone} onSave={(val) => updateWorker(worker.id, "phone", val || null)} className="text-sm text-gray-700" placeholder="Phone" />
                      </div>
                      <div className="col-span-1">
                        <EditableCell value={worker.pin} onSave={(val) => updateWorker(worker.id, "pin", val || null)} className="text-sm text-gray-700" placeholder="PIN" maxLength={4} inputMode="numeric" digitsOnly />
                      </div>
                      <div className="col-span-1">
                        <ActiveBadge active={worker.is_active} onClick={() => updateWorker(worker.id, "is_active", !worker.is_active)} />
                      </div>
                      <div className="col-span-1 text-right">
                        {worker.is_active && (
                          <button onClick={() => deleteWorker(worker.id)} className="text-xs text-gray-400 hover:text-red-600 transition-colors">
                            {(worker.timeclock_entries_aggregate?.aggregate.count ?? 0) > 0 ? "Deactivate" : "Delete"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">No workers added</div>
            )}
          </div>

          {/* Timesheets card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Timesheets</h2>
              <button
                onClick={() => setMissedShiftModalOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                + Add missed shift
              </button>
            </div>
            {timeclockEntries.length > 0 ? (
              <>
                <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100 bg-gray-50">
                  <div className="col-span-3">Worker</div>
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2">Clock in</div>
                  <div className="col-span-2">Clock out</div>
                  <div className="col-span-1">Duration</div>
                  <div className="col-span-1">Cost</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="divide-y divide-gray-100">
                  {timeclockEntries.map((entry) => {
                    const duration = entry.clock_out
                      ? (new Date(entry.clock_out).getTime() - new Date(entry.clock_in).getTime()) / 3600000
                      : null;
                    const cost = duration != null && entry.worker.hourly_rate
                      ? (duration * entry.worker.hourly_rate).toFixed(2)
                      : null;

                    return (
                      <div key={entry.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-5 py-2.5 hover:bg-gray-50 transition-colors items-center">
                        <div className="col-span-3 text-sm font-medium text-gray-700">{entry.worker.name}</div>
                        <div className="col-span-2 text-sm text-gray-600">{formatDate(entry.clock_in)}</div>
                        <div className="col-span-2">
                          <input
                            type="datetime-local"
                            className="border border-gray-300 rounded px-2 py-1 text-xs w-full focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                            defaultValue={toLocalInput(entry.clock_in)}
                            onBlur={(e) => {
                              if (e.target.value) updateEntry(entry.id, "clock_in", new Date(e.target.value).toISOString());
                            }}
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="datetime-local"
                            className="border border-gray-300 rounded px-2 py-1 text-xs w-full focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                            defaultValue={entry.clock_out ? toLocalInput(entry.clock_out) : ""}
                            onBlur={(e) => {
                              updateEntry(entry.id, "clock_out", e.target.value ? new Date(e.target.value).toISOString() : null);
                            }}
                          />
                        </div>
                        <div className="col-span-1 text-xs text-gray-600 tabular-nums">
                          {formatDuration(entry.clock_in, entry.clock_out)}
                        </div>
                        <div className="col-span-1 text-xs text-gray-600 tabular-nums">
                          {cost ? `$${cost}` : "—"}
                        </div>
                        <div className="col-span-1 text-right">
                          <button onClick={() => deleteEntry(entry.id)} className="text-xs text-gray-400 hover:text-red-600 transition-colors">Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">No timesheet data</div>
            )}
          </div>
        </>
      )}

      {!selectedVendorId && !isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center py-20 text-gray-400 text-sm">
          Select a vendor to manage their workers and roles
        </div>
      )}

      <Modal
        open={clockInModalOpen}
        onClose={() => setClockInModalOpen(false)}
        title="Clock in worker"
      >
        <ClockInForm
          events={events}
          workers={workers}
          activeShifts={activeShifts}
          onSubmit={clockInWorker}
          onCancel={() => setClockInModalOpen(false)}
        />
      </Modal>

      <Modal
        open={missedShiftModalOpen}
        onClose={() => setMissedShiftModalOpen(false)}
        title="Add missed shift"
      >
        <MissedShiftForm
          events={events}
          workers={workers}
          onSubmit={addMissedShift}
          onCancel={() => setMissedShiftModalOpen(false)}
        />
      </Modal>

      <Modal
        open={addWorkerModalOpen}
        onClose={() => setAddWorkerModalOpen(false)}
        title="Add worker"
      >
        <AddWorkerForm
          events={events}
          onSubmit={createWorker}
          onCancel={() => setAddWorkerModalOpen(false)}
        />
      </Modal>

      <Modal
        open={addRoleModalOpen}
        onClose={() => setAddRoleModalOpen(false)}
        title="Add role"
      >
        <NamePromptForm
          label="Role name"
          placeholder="e.g. Bartender"
          submitText="Add role"
          onSubmit={createRole}
          onCancel={() => setAddRoleModalOpen(false)}
        />
      </Modal>

      <Modal
        open={importClerksModalOpen}
        onClose={() => setImportClerksModalOpen(false)}
        title="Import Clerks as workers"
      >
        {clerksLoading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading clerks...</div>
        ) : clerks.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">No active clerks found in this organization.</div>
        ) : (
          <>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Event</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
                value={importEventId ?? ""}
                onChange={(e) => setImportEventId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Choose an event...</option>
                {events.map((evt) => (
                  <option key={evt.id} value={evt.id}>{evt.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <input
                type="text"
                placeholder="Search by name, username, or email..."
                value={clerkSearch}
                onChange={(e) => setClerkSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-red-500 focus:ring-red-500 bg-white"
              />
            </div>
            <div className="max-h-80 overflow-y-auto -mx-1 px-1 divide-y divide-gray-100 border-t border-b border-gray-100">
              {filteredClerks.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-400">No clerks match your search.</div>
              ) : filteredClerks.map((c) => {
                const fullName = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || c.username;
                const alreadyImported = workers.some(
                  (w) => w.name.trim().toLowerCase() === fullName.toLowerCase()
                );
                const checked = selectedClerkIds.has(c.id);
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 py-2 ${alreadyImported ? "opacity-50" : "cursor-pointer hover:bg-gray-50"}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={alreadyImported}
                      onChange={() => toggleClerkSelected(c.id)}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-700">{fullName}</div>
                      <div className="text-xs text-gray-400">
                        {c.username}
                        {c.email ? ` · ${c.email}` : ""}
                        {alreadyImported ? " · already a worker" : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-3 mt-4">
              <span className="text-xs text-gray-500">{selectedClerkIds.size} selected</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setImportClerksModalOpen(false)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={importSelectedClerks}
                  disabled={selectedClerkIds.size === 0 || importEventId == null || importing}
                  title={importEventId == null ? "Choose an event first" : ""}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {importing ? "Importing..." : "Import selected"}
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

